import type { IntegrationEndpointVariable } from '@components/integrations/integrations-types';
import { getStringValue } from '@components/integrations/lists/dynamic-options-utils';
import type { VariableValue } from '@components/text-editors/addons/field-mapping';

// An array value holds items that can themselves nest further arrays.
type SavedRequestData = { [key: string]: VariableValue | SavedRequestData[] };

type ParameterFieldContext = {
  prefix: string;
  allFields: IntegrationEndpointVariable[];
  savedRequestData?: SavedRequestData;
};

const isTopLevelField = (variableName: string, allFields: IntegrationEndpointVariable[]) =>
  allFields.some((variable) => variable.name === variableName);

const getFieldName = (variableName: string) =>
  variableName.slice(variableName.lastIndexOf('__') + 2);

// Descend one (array name, index) pair per step into the saved data, following an
// item prefix (`groups__0__items__1`) to the item it addresses.
const getItemAtPath = (
  data: SavedRequestData | undefined,
  [arrayName, index, ...rest]: string[]
): SavedRequestData | undefined => {
  if (!data || arrayName === undefined) return data;
  const nested = data[arrayName];
  const item = Array.isArray(nested) ? nested[Number(index)] : undefined;
  return getItemAtPath(item, rest);
};

// Params are stored flattened: a top-level name keys off the root prefix; an
// array-item name (`recipients__status`) adds its field name onto this item's prefix.
export const getDynamicParameterFieldId = (
  variableName: string,
  { prefix, allFields }: Omit<ParameterFieldContext, 'savedRequestData'>
): string => {
  if (isTopLevelField(variableName, allFields)) {
    const rootPrefix = prefix.split('__')[0];
    return `${rootPrefix}__${variableName}`;
  }
  return `${prefix}__${getFieldName(variableName)}`;
};

export const getDynamicParameterSavedValue = (
  variableName: string,
  { prefix, allFields, savedRequestData }: ParameterFieldContext
): string | undefined => {
  if (isTopLevelField(variableName, allFields)) {
    const saved = savedRequestData?.[variableName];
    return Array.isArray(saved) ? undefined : getStringValue(saved);
  }
  // The prefix past the root is (array name, index) pairs, one per array level.
  const item = getItemAtPath(savedRequestData, prefix.split('__').slice(1));
  return getStringValue(item?.[getFieldName(variableName)]);
};
